import React, { useEffect, useState } from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  VStack,
  Box,
  HStack,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Spinner,
  Flex
} from '@chakra-ui/react';

import { CommentEditor } from '../../CommentEditor';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import TwygsEditorNodes from '../../nodes/TwygsEditorNodes';
import getTheme from '../../themes/TwygsEditorTheme';
import { SharedHistoryContext } from '../../context/SharedHistoryContext';
import { useComments } from './hooks/useComments';
import useConvertLexicalScope from '../../hooks/useConvertLexicalScope';
import CommentItem from './components/CommentItem';
import { ChevronDownIcon } from 'lucide-react';
import CloseModalIcon from '../../../../assets/close-modal-icon.svg';
import { DataLayer } from '../../../../utils/data-layer';
import { UnauthorizedDispatcher } from 'src/utils/unauthorized-dispatcher';

type CommentSidebarProps = {
  isOpen: boolean;
  onClose: () => void;
  nodeId?: string;
  nodeText?: string;
  mediaInfo?: any; // Add mediaInfo prop
  currentUser: {
    id: string;
    name: string;
    imgUrl: string;
    firstName: string;
    isVerifiedAuthor: boolean;
    username: string;
  };
  selectedText?: string;
  setSelectedText?: any;
  commentCount?: number;
  article: any;
  articleId: string;
  notificationCommentId: string;
};

export const CommentSidebar: React.FC<CommentSidebarProps> = ({
  article,
  articleId,
  isOpen,
  onClose,
  nodeId,
  nodeText,
  mediaInfo,
  currentUser,
  selectedText,
  setSelectedText,
  commentCount,
  notificationCommentId
}) => {
  const sortCommentValue = [
    { value: 'mostRecent', label: 'Most Recent' },
    { value: 'mostRelevant', label: 'Most Relevant' }
  ];

  const editorStateRef = React.useRef(null);
  const [sortValue, setSortValue] = useState({
    value: 'mostRecent',
    label: 'Most Recent'
  });
  const convertLexicalScope = useConvertLexicalScope();
  const {
    comments,
    isLoading,
    createComment,
    updateComment,
    isReacting,
    isFetchingNextPage,
    deleteComment,
    reactToComment,
    refetchComments,
    handleScroll
  } = useComments(
    articleId,
    true,
    nodeId,
    sortValue.value,
    undefined,
    undefined,
    notificationCommentId
  );

  const [editorKey, setEditorKey] = useState(Date.now());
  const [isChecked, setIsChecked] = useState(true);
  const [flatComments, setFlatComments] = useState([]);
  const [isCreatingComment, setIsCreatingComment] = useState(false);
  const userId = JSON.parse(localStorage.getItem('user'))?.id;

  useEffect(() => {
    if (comments?.pages?.length > 0) {
      const newFlatComments = comments?.pages?.flatMap((page) => page.data) || [];
      setFlatComments(newFlatComments);
    }
  }, [comments]);

  const handleSortChange = async (newSortValue: { value: string; label: string }) => {
    setSortValue(newSortValue);
    try {
      await refetchComments();
    } catch (error) {
      console.error('Failed to fetch sorted comments:', error);
    }
  };

  const handlePost = async () => {
    setIsCreatingComment(true);
    if (editorStateRef.current) {
      const convertedLexicalScope = await convertLexicalScope({
        editorState: editorStateRef.current
      });
      try {
        await createComment({
          articleId,
          nodeId,
          content: convertedLexicalScope,
          quote: selectedText,
          isPublishProfile: isChecked
        });

        // Track paragraph comment event
        DataLayer.trackParagraphComment(article?.writingType?.name, articleId);

        refetchComments();
        setSortValue({ value: 'mostRecent', label: 'Most Recent' });
        setIsChecked(true);
        setEditorKey(Date.now());
        setSelectedText('');

        // Dispatch custom event to notify CommentableMixin
        if (nodeId) {
          window.dispatchEvent(
            new CustomEvent('commentPosted', {
              detail: {
                nodeId,
                articleId
              }
            })
          );
        }
      } catch (error) {
        console.error('Failed to post comment:', error);
      } finally {
        setIsCreatingComment(false);
      }
    }
  };

  const handleReaction = async (commentId: string, reactionType: string, isLike: boolean) => {
    try {
      if (!userId) return UnauthorizedDispatcher();

      const result = await reactToComment({
        commentId,
        nodeId,
        reactionType,
        isLike
      });

      // Update flatComments with the new reaction state from backend response
      setFlatComments((prev) => {
        return prev.map((comment) => {
          if (comment.id === commentId) {
            return {
              ...comment,
              isReacted: result.isReacted,
              reactedType: result.reactType,
              reactionNumber: result.reactionCount
            };
          }
          return comment;
        });
      });
    } catch (error) {
      console.error('Failed to react to comment:', error);
    }
  };

  const initialConfig = {
    editorState: undefined,
    namespace: 'Twygs-comment-editor',
    nodes: [...TwygsEditorNodes],
    onError: (error: Error) => {
      throw error;
    },
    theme: getTheme(true)
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xl">
      <DrawerOverlay />
      <Box overflowY={'auto'} position={'relative'} pb={'100px'}>
        <DrawerContent pb="env(safe-area-inset-bottom)">
          <DrawerHeader
            m={0}
            pt={6}
            px={{ base: '15px', md: 6 }}
            pb={0}
            flexDirection="column"
            width="100%"
            maxW={'unset'}
          >
            <HStack justify="space-between" mb={{ base: 6, md: 8 }}>
              <Text color="growth-green" fontWeight="bold" fontSize={{ base: 'md', md: 'xl' }}>
                Comments ({commentCount || 0})
              </Text>
              <img
                src={CloseModalIcon}
                onClick={onClose}
                className="h-9 w-9 cursor-pointer hover:opacity-80"
                color="growth-green"
                alt=""
              />
            </HStack>
          </DrawerHeader>

          <DrawerBody
            id="comments-sidebar-body"
            px={{ base: '15px', md: 6 }}
            pb={40}
            pt={0}
            overflowY={'auto'}
            onScroll={handleScroll}
          >
            <Box px={6} py={4} mb={{ base: 6, md: 8 }} bg="brown.50" borderRadius="4px">
              {mediaInfo && mediaInfo.length > 0 ? (
                // Display media content
                <VStack spacing={3} align="stretch">
                  {mediaInfo.map((media: any, index: number) => (
                    <Box key={index} textAlign="center" margin={'auto'}>
                      {media.type === 'image' && (
                        <img
                          src={media.src}
                          alt={media.altText}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '200px',
                            objectFit: 'contain',
                            borderRadius: '4px'
                          }}
                        />
                      )}
                      {media.type === 'video' && (
                        <video
                          src={media.src}
                          controls
                          style={{
                            maxWidth: '100%',
                            maxHeight: '200px',
                            borderRadius: '4px'
                          }}
                        />
                      )}
                      {media.type === 'audio' && (
                        <audio
                          src={media.src}
                          controls
                          style={{
                            width: '100%'
                          }}
                        />
                      )}
                      {/* {media.altText && (
                        <Text fontSize="sm" color="brown.700" mt={2}>
                          {media.altText}
                        </Text>
                      )} */}
                    </Box>
                  ))}
                  {/* {nodeText && nodeText.trim() && (
                    <Text
                      fontSize="md"
                      fontWeight={500}
                      color="brown.900"
                      lineHeight="1.5"
                      mt={2}
                    >
                      {nodeText}
                    </Text>
                  )} */}
                </VStack>
              ) : (
                // Display text content (existing behavior)
                <Text
                  className="line-clamp-4"
                  fontSize="md"
                  fontWeight={500}
                  color="brown.900"
                  lineHeight="1.5"
                  title={nodeText}
                >
                  {nodeText}
                </Text>
              )}
            </Box>
            <VStack spacing={0} align="stretch" pb={50}>
              <Box>
                <LexicalComposer initialConfig={initialConfig} key={editorKey}>
                  <SharedHistoryContext>
                    <CommentEditor
                      onPost={handlePost}
                      articleId={articleId}
                      currentUser={currentUser}
                      editorStateRef={editorStateRef}
                      isLoading={isCreatingComment}
                      shouldOnlyResetText
                      isChecked={isChecked}
                      setIsChecked={setIsChecked}
                      selectedText={selectedText}
                      onCancel={() => {}}
                    />
                  </SharedHistoryContext>
                </LexicalComposer>
              </Box>

              <Menu autoSelect={false}>
                <MenuButton
                  textAlign={'left'}
                  w={'142px'}
                  bg={'unset'}
                  h={'24px'}
                  border={'none'}
                  outline={'none'}
                  fontWeight={800}
                  _hover={{ bgColor: 'unset' }}
                  _focus={{ bgColor: 'unset' }}
                  _focusVisible={{ bgColor: 'unset' }}
                  fontSize={'md'}
                  color={'jet-black'}
                  as={Button}
                  rightIcon={<ChevronDownIcon />}
                  boxShadow={'none'}
                >
                  {sortValue.label}
                </MenuButton>
                <MenuList p={'0.625rem'} shadow={'md'}>
                  {sortCommentValue.map((option) => (
                    <MenuItem
                      _hover={{ bgColor: 'anti-flash-white' }}
                      rounded={'0.5rem'}
                      py={'8px'}
                      px={'10px'}
                      fontWeight={600}
                      _focus={{ bgColor: 'anti-flash-white' }}
                      bg={option.value === sortValue.value ? 'anti-flash-white' : 'transparent'} // Highlight selected
                      _focusVisible={{
                        bg: 'unset'
                      }}
                      key={option.value}
                      onClick={() => handleSortChange(option)}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>

              <VStack spacing={0} align="stretch" mt={'32px'}>
                {isLoading ? (
                  <Box p={4} textAlign="center">
                    <Spinner size="md" color="growth-green" />
                  </Box>
                ) : (
                  flatComments?.map((comment) => (
                    <CommentItem
                      currentUser={currentUser}
                      key={comment.id}
                      comment={comment}
                      convertLexicalScope={convertLexicalScope}
                      reactToComment={reactToComment}
                      handleReaction={handleReaction}
                      refetchComments={refetchComments}
                      updateComment={updateComment}
                      isCreatingComment={isCreatingComment}
                      isReacting={isReacting}
                      createComment={createComment}
                      deleteComment={deleteComment}
                      article={article}
                      articleId={articleId}
                      nodeId={nodeId}
                    />
                  ))
                )}
              </VStack>
            </VStack>
            {isFetchingNextPage && (
              <Flex justifyContent={'center'}>
                <Spinner size="md" color="growth-green" />
              </Flex>
            )}
          </DrawerBody>
        </DrawerContent>
      </Box>
    </Drawer>
  );
};
